import { menuController } from "@ionic/vue";
import { isDashboardPage, normalizeAppPath } from "~/utils/isDashboardPage";

export const APP_MENU_ID = "enquiries-menu";

/**
 * Whether ion-menu + split pane should render (signed in on a dashboard/admin URL).
 * Path comes from Vue Router (not window.location) so client navigations stay in sync.
 */
export function useShowAppMenu() {
  const route = useRoute();
  const router = useRouter();
  const { isAuthenticated, syncSession } = useIsAuthenticated();

  const activePath = ref(normalizeAppPath(route.path));

  const setActivePath = (path: string) => {
    activePath.value = normalizeAppPath(path);
  };

  watch(
    () => route.path,
    (path) => setActivePath(path),
    { immediate: true },
  );

  if (import.meta.client) {
    router.afterEach((to) => {
      setActivePath(to.path);
    });
  }

  const onDashboardPage = computed(() => isDashboardPage(activePath.value));

  const showAppMenu = computed(
    () => onDashboardPage.value && isAuthenticated.value,
  );

  const applyMenuVisibility = async (show: boolean) => {
    if (!import.meta.client) return;
    try {
      if (!show) {
        await menuController.close(APP_MENU_ID);
        await menuController.enable(false, APP_MENU_ID);
        return;
      }
      await menuController.enable(true, APP_MENU_ID);
    } catch {
      // Menu not mounted yet (e.g. first paint)
    }
  };

  watch(
    showAppMenu,
    (show) => {
      void applyMenuVisibility(show);
    },
    { flush: "post", immediate: true },
  );

  if (import.meta.client) {
    router.afterEach(() => {
      void applyMenuVisibility(showAppMenu.value);
    });
  }

  return {
    activePath,
    applyMenuVisibility,
    isAuthenticated,
    onDashboardPage,
    showAppMenu,
    syncSession,
  };
}
